/**
 * Admin Acquisition Page
 *
 * Where sign-ups come from, software and self-reported side by side:
 * - Sources x milestones (verified, first draft, first publish, paid) for
 *   a cohort window
 * - A sign-ups-by-source-over-time chart
 * - Drill-down to the users behind one source
 * - CSV export
 */

import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Download, Users as UsersIcon } from "@phosphor-icons/react";
import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  Spinner,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@charisol-spark/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { hasRole } from "@/components/auth/AdminGuard";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import {
  useAdminAcquisition,
  useAdminAcquisitionUsers,
} from "@/hooks/useAdmin";
import type {
  AcquisitionSourceRow,
  AcquisitionUserRow,
  AcquisitionWindow,
} from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { AcquisitionPrograms } from "./AcquisitionPrograms";

const WINDOWS: AcquisitionWindow[] = [7, 30, 90];

/** A short, chart-stable colour per source, cycling for however many are charted. */
const CHART_COLORS = [
  "hsl(262, 83%, 58%)",
  "hsl(199, 89%, 48%)",
  "hsl(142, 71%, 45%)",
  "hsl(38, 92%, 50%)",
  "hsl(0, 84%, 60%)",
  "hsl(220, 9%, 46%)",
];

function formatDay(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? day
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(parsed);
}

/** Download a CSV built from rows of equal shape — every table and drill-down on this page uses this. */
function downloadCsv(
  filename: string,
  headers: string[],
  rows: string[][]
): void {
  const escape = (cell: string) =>
    /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
  const lines = [headers, ...rows].map((row) => row.map(escape).join(","));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function sourceRowsToCsv(rows: AcquisitionSourceRow[]): string[][] {
  return rows.map((r) => [
    r.source,
    String(r.signups),
    String(r.verified),
    String(r.firstDraft),
    String(r.firstPublish),
    String(r.paid),
  ]);
}

function usersToCsv(rows: AcquisitionUserRow[]): string[][] {
  return rows.map((u) => [
    u.userId,
    u.email ?? "",
    u.name ?? "",
    u.createdAt,
    u.milestones.verifiedAt ?? "",
    u.milestones.firstDraftAt ?? "",
    u.milestones.firstPublishAt ?? "",
    u.milestones.paidAt ?? "",
  ]);
}

/** A reached milestone reads as a green pill; one not yet reached stays quiet. */
function MilestoneCell({ reached }: { reached: string | null | undefined }) {
  return reached ? (
    <StatusPill tone="green">Yes</StatusPill>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

interface DrilldownTarget {
  dimension: "software" | "selfReported";
  source: string;
}

/** The drill-down dialog: the users behind one source cell. */
function AcquisitionUsersDialog({
  target,
  days,
  onOpenChange,
}: {
  target: DrilldownTarget | null;
  days: AcquisitionWindow;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, error } = useAdminAcquisitionUsers(
    target ? { ...target, days } : null
  );
  const canSeeIdentity = hasRole("platform_admin", "platform_support");

  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{target?.source}</DialogTitle>
          <DialogDescription>
            Users who signed up in the last {days} days, attributed to this
            source.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner size="md" />
          </div>
        ) : error || !data ? (
          <Alert variant="error">
            {getErrorMessage(error, "Failed to load these users.")}
          </Alert>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {data.users.length.toLocaleString()} user
                {data.users.length === 1 ? "" : "s"}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                pill
                className="gap-1.5"
                onClick={() =>
                  downloadCsv(
                    `acquisition-${target?.dimension}-${target?.source}.csv`,
                    [
                      "User ID",
                      "Email",
                      "Name",
                      "Signed up",
                      "Verified",
                      "First draft",
                      "First publish",
                      "Paid",
                    ],
                    usersToCsv(data.users)
                  )
                }
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                Export CSV
              </Button>
            </div>
            <div className="overflow-hidden rounded-xl border border-border/50">
              <Table>
                <THead>
                  <tr>
                    {canSeeIdentity && <TH>User</TH>}
                    <TH>Signed up</TH>
                    <TH>Verified</TH>
                    <TH>First draft</TH>
                    <TH>First publish</TH>
                    <TH>Paid</TH>
                  </tr>
                </THead>
                <TBody>
                  {data.users.map((u) => (
                    <TR key={u.userId}>
                      {canSeeIdentity && (
                        <TD>
                          {u.name || u.email || (
                            <span className="font-mono text-xs text-muted-foreground">
                              {u.userId}
                            </span>
                          )}
                        </TD>
                      )}
                      <TD className="tabular-nums text-muted-foreground">
                        {formatDay(u.createdAt.slice(0, 10))}
                      </TD>
                      <TD>
                        <MilestoneCell reached={u.milestones.verifiedAt} />
                      </TD>
                      <TD>
                        <MilestoneCell reached={u.milestones.firstDraftAt} />
                      </TD>
                      <TD>
                        <MilestoneCell reached={u.milestones.firstPublishAt} />
                      </TD>
                      <TD>
                        <MilestoneCell reached={u.milestones.paidAt} />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** One sources x milestones table — reused for software and self-reported. */
function SourceTable({
  title,
  description,
  rows,
  dimension,
  onDrilldown,
}: {
  title: string;
  description: string;
  rows: AcquisitionSourceRow[];
  dimension: "software" | "selfReported";
  onDrilldown: (target: DrilldownTarget) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
      <div className="flex items-center justify-between gap-2 px-6 pt-5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          pill
          className="gap-1.5"
          onClick={() =>
            downloadCsv(
              `acquisition-${dimension}-sources.csv`,
              [
                "Source",
                "Signups",
                "Verified",
                "First draft",
                "First publish",
                "Paid",
              ],
              sourceRowsToCsv(rows)
            )
          }
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          Export CSV
        </Button>
      </div>
      <p className="mb-3 mt-1 px-6 text-xs text-muted-foreground">
        {description}
      </p>
      {rows.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title="No sign-ups in this window."
          className="py-10"
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Source</TH>
              <TH className="text-right">Signups</TH>
              <TH className="text-right">Verified</TH>
              <TH className="text-right">First draft</TH>
              <TH className="text-right">First publish</TH>
              <TH className="text-right">Paid</TH>
              <TH />
            </tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR key={row.source}>
                <TD className="font-medium">{row.source}</TD>
                <TD className="text-right tabular-nums">
                  {row.signups.toLocaleString()}
                </TD>
                <TD className="text-right tabular-nums text-muted-foreground">
                  {row.verified.toLocaleString()}
                </TD>
                <TD className="text-right tabular-nums text-muted-foreground">
                  {row.firstDraft.toLocaleString()}
                </TD>
                <TD className="text-right tabular-nums text-muted-foreground">
                  {row.firstPublish.toLocaleString()}
                </TD>
                <TD className="text-right tabular-nums text-muted-foreground">
                  {row.paid.toLocaleString()}
                </TD>
                <TD className="text-right">
                  <button
                    type="button"
                    onClick={() =>
                      onDrilldown({ dimension, source: row.source })
                    }
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    <UsersIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Users
                  </button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "signups", label: "Sign-ups", kind: "number" },
  { field: "verified", label: "Verified", kind: "number" },
  { field: "firstDraft", label: "First draft", kind: "number" },
  { field: "firstPublish", label: "First publish", kind: "number" },
  { field: "paid", label: "Paid", kind: "number" },
];

/** Biggest sources first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  filterKeys: ["days", "referral", "deal", "from", "to"],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "signups", dir: "desc" },
};

const REFERRAL_STATES = [
  { value: "", label: "Any referral stage" },
  { value: "signed_up", label: "Signed up" },
  { value: "published", label: "Published" },
  { value: "paid", label: "Paid" },
] as const;

const DEAL_STATUSES = [
  { value: "", label: "Deal on or off" },
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
] as const;

const SOURCE_GETTERS: Record<
  string,
  (row: AcquisitionSourceRow) => number | undefined
> = {
  signups: (r) => r.signups,
  verified: (r) => r.verified,
  firstDraft: (r) => r.firstDraft,
  firstPublish: (r) => r.firstPublish,
  paid: (r) => r.paid,
};

export function AdminAcquisitionPage() {
  const list = useListUrlState(URL_CONFIG);
  const requestedDays = Number(list.filters.days);
  const days: AcquisitionWindow =
    WINDOWS.find((w) => w === requestedDays) ?? 30;
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);
  const { data, isLoading, error } = useAdminAcquisition(days);

  const narrowSources = (rows: AcquisitionSourceRow[]) =>
    sortRows(
      list.search
        ? rows.filter((r) =>
            r.source.toLowerCase().includes(list.search.toLowerCase())
          )
        : rows,
      list.sort,
      SOURCE_GETTERS
    );

  const toolbar = (
    <AdminListToolbar
      label="Acquisition"
      search={{
        value: list.search,
        onChange: list.setSearch,
        placeholder: "Search a source or an account...",
      }}
      filters={[
        {
          id: "days",
          label: "Window",
          kind: "select",
          options: WINDOWS.map((w) => ({
            value: w === 30 ? "" : String(w),
            label: `Last ${w} days`,
          })),
          value: days === 30 ? "" : String(days),
          onChange: (v) => list.setFilter("days", v),
        },
        {
          id: "referral",
          label: "Referral stage",
          kind: "select",
          options: REFERRAL_STATES,
          value: list.filters.referral!,
          onChange: (v) => list.setFilter("referral", v),
        },
        {
          id: "deal",
          label: "Deal",
          kind: "select",
          options: DEAL_STATUSES,
          value: list.filters.deal!,
          onChange: (v) => list.setFilter("deal", v),
        },
        {
          id: "from",
          label: "Programme rows from",
          kind: "date",
          value: list.filters.from!,
          max: list.filters.to || undefined,
          onChange: (v) => list.setFilter("from", v),
        },
        {
          id: "to",
          label: "Programme rows to",
          kind: "date",
          value: list.filters.to!,
          min: list.filters.from || undefined,
          onChange: (v) => list.setFilter("to", v),
        },
      ]}
      sort={{
        options: SORT_OPTIONS,
        value: list.sort,
        onChange: list.setSort,
        scope: "server",
      }}
      hasActive={list.hasActive}
      onClear={list.clear}
    />
  );

  const header = (
    <PageHeader
      title="Acquisition"
      description={`Everyone who signed up in the last ${days} days, by where they came from and how far they got.`}
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {header}
        {toolbar}
        <div className="flex h-60 items-center justify-center">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        {header}
        {toolbar}
        <Alert variant="error">
          {getErrorMessage(error, "Failed to load acquisition data.")}
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      {toolbar}

      <p className="text-xs text-muted-foreground">
        Software is the first-touch cookie&apos;s source; self-reported is the
        onboarding answer. A milestone column counts the cohort that has reached
        it as of today, not only within the window.
      </p>

      <div className="rounded-xl border border-border/50 bg-card p-4 sm:p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Sign-ups by source
        </h2>
        {data.totalSignups === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title="No sign-ups in this window."
            className="py-10"
          />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={data.series}
              margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                opacity={0.5}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
                tickFormatter={(v: string) => formatDay(v)}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
                allowDecimals={false}
                width={32}
              />
              <Tooltip
                labelFormatter={(label) => formatDay(String(label))}
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              {data.seriesSources.map((source, i) => (
                <Bar
                  key={source}
                  dataKey={`bySource.${source}`}
                  name={source}
                  stackId="sources"
                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                  radius={
                    i === data.seriesSources.length - 1
                      ? [3, 3, 0, 0]
                      : undefined
                  }
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <SourceTable
        title={`Software source (${data.totalSignups.toLocaleString()} sign-ups)`}
        description="From the first-touch cookie: the UTM source on first arrival, 'Direct' with no campaign params, 'Not recorded' for an account created before this shipped."
        rows={narrowSources(data.softwareSources)}
        dimension="software"
        onDrilldown={setDrilldown}
      />

      <SourceTable
        title="Self-reported source"
        description={
          'The onboarding answer to "How did you hear about Spark?" — "Not recorded" where none exists.'
        }
        rows={narrowSources(data.selfReportedSources)}
        dimension="selfReported"
        onDrilldown={setDrilldown}
      />

      <AcquisitionPrograms
        filters={{
          search: list.search || undefined,
          referralState:
            (list.filters.referral as "signed_up" | "published" | "paid") ||
            undefined,
          dealStatus: (list.filters.deal as "on" | "off") || undefined,
          from: list.filters.from || undefined,
          to: list.filters.to || undefined,
        }}
      />

      <AcquisitionUsersDialog
        target={drilldown}
        days={days}
        onOpenChange={(open) => !open && setDrilldown(null)}
      />
    </div>
  );
}
